function captureDownloadedFile(url: string, fileName: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

const mimeTypeMap: Record<string, string> = Object.freeze({
  'application/pdf': '.pdf',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    '.docx',
  'text/csv': '.csv',
  'application/json': '.json',
  'text/plain': '.txt',
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/gif': '.gif',
  'image/svg+xml': '.svg',
  'application/zip': '.zip',
  'application/x-zip-compressed': '.zip',
});

/**
 * Maps common MIME types to their corresponding file extensions
 */
function getExtensionFromContentType(contentType: string | null): string {
  // Extract the main content type (ignore charset and other parameters)
  const mainContentType = contentType?.split(';')[0]?.trim().toLowerCase();

  if (!mainContentType) {
    return '';
  }

  const extension = mimeTypeMap[mainContentType];
  return extension ?? '';
}

/**
 * Ensures a filename has the correct extension based on the content type
 */
function ensureCorrectExtension(
  fileName: string | null,
  contentType: string | null
): string {
  const correctExtension = getExtensionFromContentType(contentType);

  // If no content type extension found, use filename as-is or default
  if (!correctExtension) {
    return fileName || 'file';
  }

  // If no filename provided, create one with the correct extension
  if (!fileName) {
    return `file${correctExtension}`;
  }

  // If filename already ends with the correct extension, use it as-is
  if (fileName.toLowerCase().endsWith(correctExtension)) {
    return fileName;
  }

  // Otherwise, replace any existing extension with the correct extension
  const fileNameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
  return `${fileNameWithoutExtension}${correctExtension}`;
}

export function downloadBlob(blob: Blob, fileName: string) {
  const blobUrl = window.URL.createObjectURL(blob);
  captureDownloadedFile(blobUrl, fileName);
  window.URL.revokeObjectURL(blobUrl);
}

export function downloadJSON(data: string, fileName: string) {
  const blob = new Blob([data], { type: 'application/json' });
  downloadBlob(blob, fileName);
}

/**
 * Downloads a file from a URL that streams the file content directly.
 *
 * @param url The URL to download from
 * @param fileName Desired download filename.  if not provided, assumes the content disposition header contains the name of the uploaded file
 * @returns Promise that resolves when download is complete
 * @throws if the network response is not ok
 */
export async function $downloadStreamedFile(
  url: string,
  fileName?: string | null
): Promise<void> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  // Extract filename from Content-Disposition header using a more robust approach.
  const contentDisposition = response.headers.get('Content-Disposition');
  if (!fileName) {
    fileName = extractFileNameFromContentDisposition(contentDisposition);
  }
  const contentType = response.headers.get('Content-Type');

  const blob = await response.blob();
  const correctedFileName = ensureCorrectExtension(fileName, contentType);
  downloadBlob(blob, correctedFileName);
}

export function extractFileNameFromContentDisposition(
  contentDisposition: string | null
): string | null {
  if (!contentDisposition) {
    return null;
  }

  const fileNameMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (fileNameMatch && fileNameMatch[1]) {
    return fileNameMatch[1];
  }
  return null;
}
